import { useState, useEffect } from "react";
import DataService from "services/DataService";

/**
 * Componente de lógica (Render Prop) responsável por gerenciar a busca,
 * carregamento e autenticação de dados de exercícios.
 * Fornece estados e funções utilitárias para os componentes filhos.
 * * @author Willian
 * @param {Object} props Propriedades do componente.
 * @param {boolean} props.open Estado que indica se o seletor está visível.
 * @param {string} props.type Tipo de exercício ('MACHINE' ou 'GRAMMAR').
 * @param {string} props.credential Credencial de autenticação do usuário.
 * @param {boolean} props.isAdmin Flag que define se a busca será via endpoint administrativo.
 * @param {Function} props.children Função de renderização (Render Prop).
 * @returns {JSX.Element} O resultado da função children com os dados injetados.
 */
export default function ExerciseSelector({
  open,
  type,
  credential,
  isAdmin,
  children,
}) {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(false);

  /**
   * Gera o objeto de configuração de cabeçalho com o Token de Autenticação.
   * Garante que o prefixo 'Bearer ' esteja presente.
   * @returns {Object} Objeto contendo o header de Authorization.
   */
  const getAuthConfig = () => {
    let auth = "";
    if (credential) {
      auth = credential;
    }

    if (auth === "") {
      return {};
    }

    let finalToken = auth;
    if (auth.indexOf("Bearer ") !== 0) {
      finalToken = "Bearer " + auth;
    }

    return {
      headers: {
        Authorization: finalToken,
      },
    };
  };

  /**
   * Realiza a chamada ao serviço de dados para listar os exercícios conforme o perfil.
   */
  const loadData = async () => {
    if (!credential) {
      return;
    }

    setLoading(true);
    try {
      const config = getAuthConfig();
      let response = null;

      // Seleção de endpoint baseada no papel do usuário
      if (isAdmin) {
        response = await DataService.listExercisesAdmin(type, config);
      } else {
        response = await DataService.listExercisesUser(type, config);
      }

      if (response) {
        if (response.data) {
          setExercises(response.data);
        } else {
          setExercises([]);
        }
      } else {
        setExercises([]);
      }
    } catch (err) {
      console.error("Erro ao carregar lista de exercícios:", err);
      setExercises([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Hook de efeito que dispara a carga de dados ao abrir o seletor ou mudar credenciais.
   */
  useEffect(() => {
    if (open) {
      if (credential) {
        loadData();
      }
    }
  }, [open, type, credential]);

  /**
   * Retorna a execução da função children passando o estado e funções de controle.
   */
  return children({
    exercises,
    loadData,
    loading,
    getAuthConfig,
  });
}
